/**
 * Design Actions
 *
 * Actions for designing and modifying database objects.
 *
 * "Design Table" opens the buffer-backed Table Designer (VqeDdlEdit Slice 0.5):
 * the alter-mode entry synthesizes a CREATE script from the existing table's
 * metadata via the engine's script provider, then seeds `initialSql` /
 * `currentSql` so the Designer's hidden buffer is populated on mount.
 */

import { Settings, GitBranch } from 'lucide-react';
import type { ObjectActionDefinition, ObjectActionContext, SchemaObjectType } from '../types';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { toast } from '../../../../components/ui/toaster';
import { resolveDefaultDependencyViewMode } from '../../../workspace/dependency-viewer/utils/dependencyViewModeDefault';
import type { DependencyObjectType } from '../../../workspace/core/types/workspaceTypes';
import { synthesizeAlterTableBuffer } from '../../../workspace/table-designer/services/synthesizeBuffer';
import type { UserFacingEngineId } from '../../../../core/engines';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/**
 * Design Table action - opens table designer in alter mode
 */
export const designTableAction: ObjectActionDefinition = {
  id: 'design',
  label: 'Design Table...',
  icon: Settings,
  category: 'design',
  appliesTo: ['table'],
  isPrimary: true,
  execute: async (context: ObjectActionContext) => {
    const { activeConnection, connection } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    const engine = (activeConnection.connection.engine as UserFacingEngineId) || 'mssql';
    const { addTab, updateTabContent } = useWorkspaceStore.getState();

    const tabId = addTab({
      title: `${context.objectName} [Design]`,
      type: 'table-designer',
      content: {
        mode: 'alter',
        tableName: context.objectName,
        schema: context.schemaName,
        database: context.databaseName,
        isLoading: true,
      },
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: context.databaseName,
      },
    });

    const result = await synthesizeAlterTableBuffer(
      {
        engine,
        database: context.databaseName,
        schema: context.schemaName,
        tableName: context.objectName,
      },
      context.connectionId,
    );

    if (result.error) {
      updateTabContent(tabId, {
        error: result.error,
        isLoading: false,
      });
      return;
    }

    updateTabContent(tabId, {
      initialSql: result.sql ?? undefined,
      currentSql: result.sql ?? undefined,
      isLoading: false,
    });
  },
};

/**
 * View Dependencies action - opens dependency viewer tab
 */
export const viewDependenciesAction: ObjectActionDefinition = {
  id: 'view-dependencies',
  label: 'Dependencies',
  icon: GitBranch,
  category: 'design',
  appliesTo: ['table', 'view', 'procedure', 'function'],
  isPrimary: true,
  execute: (context: ObjectActionContext) => {
    const { activeConnection, connection, engine } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
    const resolvedSchema = context.schemaName ?? defaultSchema;

    // Map SchemaObjectType to dependency viewer object type
    const objectTypeMap: Record<SchemaObjectType, DependencyObjectType> = {
      table: 'table',
      view: 'view',
      procedure: 'procedure',
      function: 'function',
      trigger: 'trigger',
      package: 'procedure',
    };

    const { addTab } = useWorkspaceStore.getState();
    const dependencyObjectType = objectTypeMap[context.objectType];

    addTab({
      title: `Dependencies: ${context.objectName}`,
      type: 'dependency-viewer',
      content: {
        objectName: context.objectName,
        objectType: dependencyObjectType,
        schema: resolvedSchema,
        database: context.databaseName,
        // F09/B9: tables (and, once the fetch resolves, any object with a
        // large dependency count) default to tree — the readable view.
        // Count is unknown here, so this only ever affects the table branch;
        // `DependencyViewerTab`'s fetch effect re-applies the same heuristic
        // once the real count is known.
        viewMode: resolveDefaultDependencyViewMode(dependencyObjectType, 0),
        isLoading: true,
      },
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: context.databaseName,
      },
    });
  },
};

/**
 * All design actions
 */
export const designActions: ObjectActionDefinition[] = [
  designTableAction,
  viewDependenciesAction,
];
